import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, FileText, Search, Filter, CheckCircle, XCircle,
  Eye, Download, Clock, User, Calendar, Loader2, AlertTriangle,
  FileCheck, FileClock, Files, Trash2, RefreshCw, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPapersManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pending');
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSubject, setFilterSubject] = useState('all');
  const [filterStage, setFilterStage] = useState('all');
  
  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  
  // Rejection modal state
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchPapers();
  }, [activeTab]);

  const fetchPapers = async () => {
    setLoading(true);
    try {
      let endpoint = activeTab === 'pending' 
        ? `${API}/api/admin/pending-papers`
        : `${API}/api/admin/all-papers`;
      
      const response = await axios.get(endpoint, { headers });
      setPapers(response.data.papers || []);
    } catch (error) {
      console.error('Error fetching papers:', error);
      toast.error('Failed to fetch papers');
    }
    setLoading(false);
  };

  const handlePreview = async (paper) => {
    setSelectedPaper(paper);
    setPreviewOpen(true);
    setPreviewLoading(true);
    
    try {
      const response = await axios.get(
        `${API}/api/admin/paper/${paper.id}/preview`,
        { headers }
      );
      setPreviewData(response.data);
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error('Failed to load paper preview');
      setPreviewData({ error: 'Failed to load preview' });
    }
    setPreviewLoading(false);
  };

  const handleApprove = async (paper) => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/api/admin/paper/${paper.id}/approve`,
        {},
        { headers }
      );
      toast.success(`Paper "${paper.name}" approved successfully!`);
      fetchPapers();
      setPreviewOpen(false);
    } catch (error) {
      toast.error('Failed to approve paper');
    }
    setActionLoading(false);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/api/admin/paper/${selectedPaper.id}/reject`,
        { reason: rejectReason },
        { headers }
      );
      toast.success(`Paper rejected. Tutor will be notified.`);
      fetchPapers();
      setRejectOpen(false);
      setPreviewOpen(false);
      setRejectReason('');
    } catch (error) {
      toast.error('Failed to reject paper');
    }
    setActionLoading(false);
  };

  const handleDownload = async (paper) => {
    try {
      const response = await axios.get(
        `${API}/api/papers/${paper.id}/download`,
        { headers, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', paper.fileName || `${paper.name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download paper');
    }
  };

  // Filter papers
  const filteredPapers = papers.filter(paper => {
    const matchesSearch = paper.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          paper.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSubject = filterSubject === 'all' || paper.subject === filterSubject;
    const matchesStage = filterStage === 'all' || paper.stage === filterStage;
    return matchesSearch && matchesSubject && matchesStage;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30"><FileClock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-500/20 text-green-300 border-green-500/30"><FileCheck className="w-3 h-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500/20 text-red-300 border-red-500/30"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-300">{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/question-banks')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
                <Files className="w-8 h-8 text-blue-400" />
                Papers Management
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                Review, approve, and manage uploaded papers
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={fetchPapers}
            className="border-gray-600"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-gray-800/50 border border-gray-700">
            <TabsTrigger value="pending" className="data-[state=active]:bg-amber-600">
              <FileClock className="w-4 h-4 mr-2" /> Pending Review
            </TabsTrigger>
            <TabsTrigger value="all" className="data-[state=active]:bg-blue-600">
              <Files className="w-4 h-4 mr-2" /> All Papers
            </TabsTrigger>
          </TabsList>

          {/* Filters */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search papers..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 bg-gray-700/50 border-gray-600"
                    />
                  </div>
                </div>
                <Select value={filterSubject} onValueChange={setFilterSubject}>
                  <SelectTrigger className="w-[150px] bg-gray-700/50 border-gray-600">
                    <SelectValue placeholder="Subject" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Subjects</SelectItem>
                    <SelectItem value="maths">Maths</SelectItem>
                    <SelectItem value="english">English</SelectItem>
                    <SelectItem value="science">Science</SelectItem>
                    <SelectItem value="verbal-reasoning">Verbal Reasoning</SelectItem>
                    <SelectItem value="non-verbal-reasoning">Non-Verbal Reasoning</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterStage} onValueChange={setFilterStage}>
                  <SelectTrigger className="w-[150px] bg-gray-700/50 border-gray-600">
                    <SelectValue placeholder="Stage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Stages</SelectItem>
                    <SelectItem value="7-plus">7+</SelectItem>
                    <SelectItem value="11-plus">11+</SelectItem>
                    <SelectItem value="gcse">GCSE</SelectItem>
                    <SelectItem value="a-level">A-Level</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Papers List */}
          <TabsContent value="pending" className="mt-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
              </div>
            ) : filteredPapers.length === 0 ? (
              <Card className="bg-gray-800/50 border-gray-700">
                <CardContent className="p-12 text-center">
                  <FileClock className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Pending Papers</h3>
                  <p className="text-gray-400">All papers have been reviewed. Check back later!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredPapers.map((paper) => (
                  <Card key={paper.id} className="bg-gray-800/50 border-gray-700 hover:border-gray-600 transition-colors">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="p-3 rounded-xl bg-amber-500/20">
                            <FileText className="w-6 h-6 text-amber-400" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-white">{paper.name}</h3>
                            <p className="text-gray-400 text-sm mt-1">{paper.description}</p>
                            <div className="flex flex-wrap gap-2 mt-3">
                              {getStatusBadge(paper.status)}
                              <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                                {paper.subject}
                              </Badge>
                              <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                                {paper.stage}
                              </Badge>
                              <Badge className="bg-gray-500/20 text-gray-300 border-gray-500/30">
                                {paper.paperType}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                {paper.uploadedByName || paper.creatorName || 'Unknown'}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {new Date(paper.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePreview(paper)}
                            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                          >
                            <Eye className="w-4 h-4 mr-1" /> Preview
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownload(paper)}
                            className="border-gray-600"
                          >
                            <Download className="w-4 h-4 mr-1" /> Download
                          </Button>
                          {paper.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleApprove(paper)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" /> Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => {
                                  setSelectedPaper(paper);
                                  setRejectOpen(true);
                                }}
                              >
                                <XCircle className="w-4 h-4 mr-1" /> Reject
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="all" className="mt-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
              </div>
            ) : filteredPapers.length === 0 ? (
              <Card className="bg-gray-800/50 border-gray-700">
                <CardContent className="p-12 text-center">
                  <Files className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Papers Found</h3>
                  <p className="text-gray-400">No papers match your filters.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredPapers.map((paper) => (
                  <Card key={paper.id} className="bg-gray-800/50 border-gray-700 hover:border-gray-600 transition-colors">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`p-3 rounded-xl ${
                            paper.status === 'approved' ? 'bg-green-500/20' :
                            paper.status === 'pending' ? 'bg-amber-500/20' : 'bg-red-500/20'
                          }`}>
                            <FileText className={`w-6 h-6 ${
                              paper.status === 'approved' ? 'text-green-400' :
                              paper.status === 'pending' ? 'text-amber-400' : 'text-red-400'
                            }`} />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-white">{paper.name}</h3>
                            <p className="text-gray-400 text-sm mt-1">{paper.description}</p>
                            <div className="flex flex-wrap gap-2 mt-3">
                              {getStatusBadge(paper.status)}
                              <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                                {paper.subject}
                              </Badge>
                              <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                                {paper.stage}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                {paper.uploadedByName || paper.creatorName || 'Unknown'}
                              </span>
                              <span className="flex items-center gap-1">
                                <Eye className="w-4 h-4" />
                                {paper.views || 0} views
                              </span>
                              <span className="flex items-center gap-1">
                                <Download className="w-4 h-4" />
                                {paper.downloads || 0} downloads
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePreview(paper)}
                            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                          >
                            <Eye className="w-4 h-4 mr-1" /> Preview
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownload(paper)}
                            className="border-gray-600"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-400" />
              Paper Preview: {selectedPaper?.name}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Review the paper content and quality before approving
            </DialogDescription>
          </DialogHeader>
          
          {previewLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
              <span className="ml-3 text-gray-400">Loading preview...</span>
            </div>
          ) : previewData?.error ? (
            <div className="p-6 text-center">
              <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <p className="text-gray-400">{previewData.error}</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => handleDownload(selectedPaper)}
              >
                <Download className="w-4 h-4 mr-2" /> Download to View
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Paper Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-700/30 rounded-lg">
                <div>
                  <p className="text-sm text-gray-400">Subject</p>
                  <p className="text-white font-medium">{selectedPaper?.subject}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Stage</p>
                  <p className="text-white font-medium">{selectedPaper?.stage}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Type</p>
                  <p className="text-white font-medium">{selectedPaper?.paperType}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Uploaded By</p>
                  <p className="text-white font-medium">{selectedPaper?.uploadedByName || selectedPaper?.creatorName || 'Unknown'}</p>
                </div>
              </div>

              {/* Description */}
              {selectedPaper?.description && (
                <div className="p-4 bg-gray-700/30 rounded-lg">
                  <p className="text-sm text-gray-400 mb-1">Description</p>
                  <p className="text-white">{selectedPaper.description}</p>
                </div>
              )}

              {/* Preview Content */}
              {previewData?.questions && previewData.questions.length > 0 ? (
                <div className="p-4 bg-gray-700/30 rounded-lg">
                  <p className="text-sm text-gray-400 mb-3">Sample Questions ({previewData.questions.length} total)</p>
                  <div className="space-y-4">
                    {previewData.questions.slice(0, 5).map((q, idx) => (
                      <div key={idx} className="p-3 bg-gray-800/50 rounded-lg">
                        <p className="text-white font-medium">Q{idx + 1}: {q.question}</p>
                        {q.options && (
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            {q.options.map((opt, i) => (
                              <span key={i} className={`text-sm ${i === q.correct_answer ? 'text-green-400' : 'text-gray-400'}`}>
                                {String.fromCharCode(65 + i)}) {opt}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    {previewData.questions.length > 5 && (
                      <p className="text-gray-500 text-sm text-center">
                        ... and {previewData.questions.length - 5} more questions
                      </p>
                    )}
                  </div>
                </div>
              ) : previewData?.fileUrl ? (
                <div className="p-4 bg-gray-700/30 rounded-lg text-center">
                  <p className="text-gray-400 mb-4">PDF Preview</p>
                  <iframe
                    src={previewData.fileUrl}
                    className="w-full h-96 rounded-lg border border-gray-600"
                    title="Paper Preview"
                  />
                </div>
              ) : (
                <div className="p-6 text-center">
                  <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">Preview not available. Download to view the full paper.</p>
                </div>
              )}
            </div>
          )}

          {selectedPaper?.status === 'pending' && (
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setPreviewOpen(false)}
                className="border-gray-600"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setRejectOpen(true);
                }}
              >
                <XCircle className="w-4 h-4 mr-2" /> Reject
              </Button>
              <Button
                onClick={() => handleApprove(selectedPaper)}
                disabled={actionLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Approve Paper
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Rejection Modal */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="bg-gray-800 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-400" />
              Reject Paper
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Please provide a reason for rejection. This will be shared with the tutor.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Textarea
              placeholder="Reason for rejection (e.g., poor quality, incorrect format, incomplete content...)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="bg-gray-700/50 border-gray-600 min-h-[100px]"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectOpen(false);
                setRejectReason('');
              }}
              className="border-gray-600"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={actionLoading || !rejectReason.trim()}
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Confirm Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPapersManagement;
